import { useAtom, useSetAtom } from 'jotai'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'
import { useSetRecoilState } from 'recoil'
import { internalGooglePlaceId, selectedLocationId } from 'src/atoms/search'
import { AMGeometryPolygon } from 'src/common-services'
import { AMGeometryMultiPolygon } from 'src/common-services'
import { Circle } from 'src/types/map'
import { browserLogger } from 'src/utils/browser-logger'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { useCachedAtomValue } from 'src/utils/jotai'
import * as coreAtoms from 'src/v2/state/core'
import * as searchAtoms from 'src/v2/state/map-explorer/search'
import * as sidebarAtoms from 'src/v2/state/owner-reveal/sidebar'
import { SUPPORTED_IDS } from 'src/v2/types/core'

type SelectOptions = {
  keepCurrentPath?: boolean
  newTab?: boolean
}

type ClearSelectedOptions = {
  keepSearchResults?: boolean
}

export const useMapExplorerNavigation = () => {
  const router = useRouter()
  const [searchEntity, setSearchEntity] = useAtom(coreAtoms.searchEntity)
  const setLocationId = useSetRecoilState(selectedLocationId)
  const setGooglePlaceId = useSetRecoilState(internalGooglePlaceId)
  const searchResults = useCachedAtomValue(searchAtoms.searchResults, undefined)
  const entitiesResults = useCachedAtomValue(sidebarAtoms.entitiesResults, undefined)
  const setAddressesResults = useSetAtom(searchAtoms.addressesResults)
  const setOwnerResults = useSetAtom(sidebarAtoms.entitiesResults)
  const isOwnerRevealEnabled = isFeatureEnabled('owner-reveal')

  const basePath = useMemo(() => (router.asPath.includes('owner-reveal') ? '/owner-reveal' : '/'), [router.asPath])
  const currentPath = useMemo(() => {
    return router.asPath.split('?')[0]
  }, [router.asPath])

  const selectLocation = async (locationId: string, options?: SelectOptions) => {
    const path = options?.keepCurrentPath ? currentPath : basePath
    browserLogger.debug('selecting location', locationId, path)
    await router.push(`${path}?id.am-loc=${encodeURIComponent(locationId)}`)
  }

  const selectAddress = async (placeId: string, options?: SelectOptions) => {
    const path = options?.keepCurrentPath ? currentPath : basePath
    browserLogger.debug('selecting address(google)', placeId, path)
    await router.push(`${path}?id.google=${encodeURIComponent(placeId)}`)
  }

  const selectBrand = async (brandId: string, options?: SelectOptions) => {
    const path = options?.keepCurrentPath ? currentPath : basePath
    browserLogger.debug('selecting brand(alphamap)', brandId, path)
    await router.push(`${path}?id.am-brand=${encodeURIComponent(brandId)}`)
  }

  const selectArea = (area: AMGeometryPolygon | AMGeometryMultiPolygon | Circle | undefined) => {
    setSearchEntity((prev) => ({
      ...prev,
      geometry: area,
    }))
  }

  const selectParcel = async (parcelId: string, options?: SelectOptions) => {
    if (!isOwnerRevealEnabled) {
      return
    }

    const path = options?.keepCurrentPath ? currentPath : basePath
    browserLogger.debug('selecting parcel', parcelId, path)
    await openUrl(`${path}?id.am-opa=${encodeURIComponent(parcelId)}`, options?.newTab)
  }

  const selectOwner = async (ownerId: string, options?: SelectOptions) => {
    if (!isOwnerRevealEnabled) {
      return
    }
    const path = options?.keepCurrentPath ? currentPath : basePath
    browserLogger.debug('selecting owner', ownerId, path)
    await openUrl(`${path}?id.am-oen=${encodeURIComponent(ownerId)}`, options?.newTab)
  }

  const openUrl = async (url: string, newTab = false) => {
    if (newTab) {
      return window.open(url, '_blank')
    }
    await router.push(url)
  }

  const clearSelected = useCallback(
    async (options?: ClearSelectedOptions) => {
      browserLogger.debug('clearing selected', basePath)
      await router.push(basePath)
      setSearchEntity({
        ids: {
          'am-loc': undefined,
          google: undefined,
          'am-brand': undefined,
          'am-opa': undefined,
          'am-oen': undefined,
        },
      })
      setLocationId(undefined)
      setGooglePlaceId(undefined)
      if (!options?.keepSearchResults) {
        setAddressesResults(undefined)
        setOwnerResults(undefined)
      }
    },
    [router.pathname]
  )

  const goBack = async () => {
    if (window.location.pathname.includes('insights')) {
      // next js also saves url params in the query object so we need to make sure we only keep the ids
      const query = { ...router.query }
      Object.keys(query).forEach((key) => {
        const id = key.split('.')[1] // eg id.am-loc -> am-loc
        if (!Object.values(SUPPORTED_IDS).includes(id as any)) {
          delete query[key]
        }
      })
      await router.push({ pathname: basePath, query })
    } else {
      await clearSelected({ keepSearchResults: true })
    }
  }

  const getBackText = () => {
    if (window.location.pathname.includes('insights')) {
      return 'Back'
    }

    if (
      searchEntity.ids['am-opa'] ||
      searchEntity.ids['am-loc'] ||
      searchEntity.ids['google'] ||
      searchEntity.ids['am-brand']
    ) {
      if (searchResults) {
        return 'Back to search results'
      }
      return 'Back'
    }

    if (searchEntity.ids['am-oen']) {
      if (entitiesResults) {
        return 'Back to search results'
      }
      return 'Back'
    }

    return 'Back'
  }

  return {
    selectLocation,
    selectAddress,
    selectBrand,
    selectArea,
    selectParcel,
    selectOwner,
    clearSelected,
    getBackText,
    goBack,
  }
}
